import React from 'react';
import { useTranslation } from 'react-i18next';
import { Spin, Empty } from 'antd';
import { DragDropContext } from 'react-beautiful-dnd';
import { SearchOutlined } from '@ant-design/icons';
import Button from '../../UI/buttons/Button';
import SettingsMenu from '../../components/settings/SettingsMenu';
import {
    PageWrapper,
    FormWrapper,
    ControlsRow,
    SearchWrapper,
    SearchInput,
    ButtonsContainer
} from './styles';
import StageGroup from './components/StageGroup';
import ActionToolbar from './components/ActionToolbar';
import { useStageMapping } from './hooks/useStageMapping';

const DefaultStageMappings: React.FC = () => {
    const { t } = useTranslation();
    const {
        loading,
        saving,
        atsConnected,
        noJobsConnected,
        originalStages,
        filteredGroups,
        searchQuery,
        selectedStages,
        selectedCount,
        addedCount,
        updatedCount,
        unmappedCount,
        setSearchQuery,
        toggleGroupExpansion,
        toggleStageSelection,
        handleDragEnd,
        applyStageTypeToSelected,
        handleSaveConfirm,
        handleResetChanges,
        clearSelection,
        toggleAlwaysUnmap
    } = useStageMapping();

    // Render loading state
    if (loading) {
        return (
            <PageWrapper>
                <FormWrapper>
                    <SettingsMenu menu="ats" />
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '2rem'
                    }}>
                        <Spin size="large" />
                    </div>
                </FormWrapper>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper>
            <FormWrapper>
                <SettingsMenu menu="ats" />

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '2rem'
                    }}>
                        <div>
                            <h1 style={{
                                fontSize: '24px',
                                fontWeight: 'bold',
                                margin: 0
                            }}>
                                {t('ats.DEFAULT_MAPPINGS_TITLE')}
                            </h1>
                            <p style={{
                                fontSize: '14px',
                                color: '#637381',
                                margin: '0.5rem 0 0 0'
                            }}>
                                {t('ats.DEFAULT_MAPPINGS_DESCRIPTION')}
                            </p>
                        </div>
                    </div>

                    {/* --- Top Controls Row --- */}
                    <ControlsRow>
                        {/* Search input */}
                        <SearchWrapper style={{ marginBottom: 0 }}>
                            <SearchInput
                                placeholder={t('ats.SEARCH_STAGES')}
                                prefix={<SearchOutlined />}
                                value={searchQuery}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                                allowClear
                            />
                        </SearchWrapper>

                        {/* Buttons section moved inside ControlsRow */}
                        <ButtonsContainer style={{ marginTop: 0, marginBottom: 0 }}>
                            <Button
                                onClick={handleResetChanges}
                                disabled={addedCount === 0 && updatedCount === 0 && unmappedCount === 0}
                            >
                                {t('RESET')}
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleSaveConfirm}
                                loading={saving}
                                disabled={addedCount === 0 && updatedCount === 0 && unmappedCount === 0}
                            >
                                {t('SAVE_MAPPINGS')}
                            </Button>
                        </ButtonsContainer>
                    </ControlsRow>

                    {/* No ATS connected message */}
                    {!atsConnected ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '2rem',
                            backgroundColor: '#ffffff',
                            border: '1px solid #dae5ec',
                            borderRadius: '8px'
                        }}>
                            <p style={{
                                fontSize: '16px',
                                color: '#637381',
                                textAlign: 'center'
                            }}>
                                {t('ats.NO_ATS_CONNECTED')}
                            </p>
                        </div>
                    ) : noJobsConnected ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '2rem',
                            backgroundColor: '#ffffff',
                            border: '1px solid #dae5ec',
                            borderRadius: '8px'
                        }}>
                            <p style={{
                                fontSize: '16px',
                                color: '#637381',
                                textAlign: 'center'
                            }}>
                                {t('ats.NO_STAGES_FOUND')}
                            </p>
                        </div>
                    ) : originalStages.length === 0 ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '2rem',
                            backgroundColor: '#ffffff',
                            border: '1px solid #dae5ec',
                            borderRadius: '8px'
                        }}>
                            <p style={{
                                fontSize: '16px',
                                color: '#637381',
                                textAlign: 'center'
                            }}>
                                {t('ats.NO_STAGES_FOUND')}
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Action bar for bulk operations */}
                            <ActionToolbar
                                selectedCount={selectedCount}
                                onApplyStageType={applyStageTypeToSelected}
                                onClearSelection={clearSelection}
                            />

                            {/* Stage groups */}
                            {filteredGroups.length === 0 ? (
                                <Empty
                                    description={<span style={{ color: '#888', fontSize: '13px' }}>{t('ats.NO_SEARCH_RESULTS')}</span>}
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    style={{ margin: '40px 0' }}
                                />
                            ) : (
                                <DragDropContext onDragEnd={handleDragEnd}>
                                    {filteredGroups
                                        .map((group, groupIndex) => (
                                            <StageGroup
                                                key={group.stageType}
                                                stageType={group.stageType}
                                                label={group.label}
                                                stages={group.stages}
                                                isExpanded={group.isExpanded}
                                                selectedStages={selectedStages}
                                                onToggleExpand={() => toggleGroupExpansion(groupIndex)}
                                                onToggleSelection={toggleStageSelection}
                                                onToggleAlwaysUnmap={toggleAlwaysUnmap}
                                            />
                                        ))}
                                </DragDropContext>
                            )}
                        </>
                    )}
                </div>
            </FormWrapper>
        </PageWrapper>
    );
};

export default DefaultStageMappings; 